import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { getAdvisorSuggestOpportunitiesLogs } from '../../services/advisor_services';

const PAGE_SIZE = 10;

const useStyles = () => ({
  container: {
    width: '100%',
    paddingLeft: '170px',
    paddingRight: '170px',
    paddingTop: '25px',
    paddingBottom: '40px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  form: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '16px',
    flexWrap: 'wrap',
  },
  logsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  card: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    backgroundColor: '#fafafa',
  },
  cardHeader: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
  },
  preformattedBlock: {
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    padding: '12px',
    maxHeight: '200px',
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: 'Menlo, Monaco, Consolas, "Courier New", monospace',
    fontSize: '0.75rem',
    margin: 0,
  },
  loadMoreContainer: {
    marginTop: '8px',
  },
  errorText: {
    color: '#d32f2f',
  },
});

function formatDateTime(value) {
  if (!value) {
    return '—';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString();
}

function parseJson(jsonValue) {
  if (!jsonValue) {
    return null;
  }

  try {
    return JSON.parse(jsonValue);
  } catch (error) {
    return jsonValue;
  }
}

function prettyPrintJson(jsonValue) {
  const parsed = parseJson(jsonValue);

  if (parsed === null) {
    return '—';
  }

  if (typeof parsed === 'string') {
    return parsed;
  }

  return JSON.stringify(parsed, null, 2);
}

function extractRecommendedBoards(log) {

  return [
    {
      board: log?.recommendedBoard1,
      company: log?.recommendedBoard1Company,
    },
    {
      board: log?.recommendedBoard2,
      company: log?.recommendedBoard2Company,
    },
    {
      board: log?.recommendedBoard3,
      company: log?.recommendedBoard3Company,
    },
  ].filter((entry) => entry.board || entry.company);
}

function renderArrayValue(value) {
  if (!value) {
    return '—';
  }

  if (Array.isArray(value)) {
    return value.filter(Boolean).join(', ') || '—';
  }

  return String(value);
}

function formatMillisecondsToSeconds(value) {
  if (value === null || value === undefined) {
    return '—';
  }

  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return '—';
  }

  const seconds = numericValue / 1000;

  return `${seconds.toFixed(2)}s`;
}

function SuggestOpportunitiesLogs() {
  const classes = useStyles();
  const [advisorId, setAdvisorId] = useState('');
  const [logs, setLogs] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);

  async function fetchLogs({ token = null, reset = false } = {}) {
    const shouldReset = reset || !token;

    if (shouldReset) {
      setLogs([]);
      setNextToken(null);
      setHasLoaded(false);
    }

    setError('');

    if (token) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }

    try {
      const response = await getAdvisorSuggestOpportunitiesLogs({
        advisorId: advisorId.trim() || undefined,
        limit: PAGE_SIZE,
        nextToken: token || undefined,
      });

      const connection = response?.data?.getSuggestOpportunitiesLogs;
      const items = connection?.items || [];

      setLogs((prev) => (token ? [...prev, ...items] : items));
      setNextToken(connection?.nextToken || null);
      setHasLoaded(true);
    } catch (fetchError) {
      console.error('Error fetching suggestion logs:', fetchError);
      setError('Unable to load logs. Please try again.');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }

  function handleSubmit(event) {
    event.preventDefault();
    fetchLogs({ reset: true });
  }

  useEffect(() => {
    fetchLogs({ reset: true });
  }, []);

  return (
    <>
      <Helmet>
        <title>Suggestion Logs</title>
      </Helmet>
      <div style={classes.container}>
        <Typography variant="h5" component="h1">
          Advisor Suggest Opportunities Logs
        </Typography>

        <form style={classes.form} onSubmit={handleSubmit}>
          <TextField
            label="Advisor ID"
            variant="outlined"
            size="small"
            value={advisorId}
            onChange={(event) => setAdvisorId(event.target.value)}
          />
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? 'Loading...' : 'Search logs'}
          </Button>
        </form>

        {error ? (
          <Typography variant="body2" sx={classes.errorText}>
            {error}
          </Typography>
        ) : null}

        {isLoading && !isLoadingMore ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading results...</Typography>
          </Box>
        ) : null}

        <div style={classes.logsWrapper}>
          {logs.map((log) => {
            const recommendedBoards = extractRecommendedBoards(log);
            return (
              <Paper key={log.id} elevation={0} sx={classes.card}>
                <Box sx={classes.cardHeader}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Created At
                    </Typography>
                    <Typography variant="body2">
                      {formatDateTime(log.createdAt)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Status
                    </Typography>
                    <Typography variant="body2">{log.status || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Trigger
                    </Typography>
                    <Typography variant="body2">{log.trigger || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Model
                    </Typography>
                    <Typography variant="body2">{log.model || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      OpenAI Execution (s)
                    </Typography>
                    <Typography variant="body2">
                      {formatMillisecondsToSeconds(log.openAiExecutionMs)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total Execution (s)
                    </Typography>
                    <Typography variant="body2">
                      {formatMillisecondsToSeconds(log.totalExecutionMs)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Advisor ID
                    </Typography>
                    <Typography variant="body2">{log.advisorId || '—'}</Typography>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Prompt
                  </Typography>
                  <Typography variant="body2" component="pre" sx={classes.preformattedBlock}>
                    {prettyPrintJson(log.prompt)}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Request Payload
                  </Typography>
                  <Typography variant="body2" component="pre" sx={classes.preformattedBlock}>
                    {prettyPrintJson(log.requestPayload)}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Response Payload
                  </Typography>
                  <Typography variant="body2" component="pre" sx={classes.preformattedBlock}>
                    {prettyPrintJson(log.responsePayload)}
                  </Typography>
                </Box>

                {recommendedBoards.length > 0 ? (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Recommended Boards
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {recommendedBoards.map((entry, index) => {
                        if (!entry) return null;

                        const { board, company } = entry;

                        return (
                          <Box
                            key={`log-${log.id}-recommended-${index}`}
                            sx={{
                              borderLeft: '3px solid #e0e0e0',
                              pl: 1,
                              mb: 1,
                            }}
                          >
                            {board ? (
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, display: 'block' }}
                              >
                                {board}
                              </Typography>
                            ) : null}

                            {company ? (
                              <Box sx={{ mt: 0.5 }}>
                                {company.displayName ? (
                                  <Typography variant="caption" sx={{ display: 'block' }}>
                                    Company: {company.displayName}
                                  </Typography>
                                ) : null}
                                {company.headline ? (
                                  <Typography variant="caption" sx={{ display: 'block' }}>
                                    Headline: {company.headline}
                                  </Typography>
                                ) : null}
                                {company.storyAndMission ? (
                                  <Typography variant="caption" sx={{ display: 'block' }}>
                                    Mission: {company.storyAndMission}
                                  </Typography>
                                ) : null}
                                {company.industryTagIds ? (
                                  <Typography variant="caption" sx={{ display: 'block' }}>
                                    Industries: {renderArrayValue(company.industryTagIds)}
                                  </Typography>
                                ) : null}
                                {company.desiredSkillsTagIds ? (
                                  <Typography variant="caption" sx={{ display: 'block' }}>
                                    Desired Skills: {renderArrayValue(company.desiredSkillsTagIds)}
                                  </Typography>
                                ) : null}
                                {Array.isArray(company.benefits) && company.benefits.length > 0 ? (
                                  <Box sx={{ mt: 0.5 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{ display: 'block', fontWeight: 500 }}
                                    >
                                      Benefits:
                                    </Typography>
                                    {company.benefits.map((benefit, benefitIndex) => (
                                      <Typography
                                        key={`benefit-${benefitIndex}`}
                                        variant="caption"
                                        sx={{ display: 'block', ml: 1 }}
                                      >
                                        {`${benefit?.category || 'General'}: ${
                                          benefit?.details || '—'
                                        }${benefit?.featured ? ' (featured)' : ''}`}
                                      </Typography>
                                    ))}
                                  </Box>
                                ) : null}
                              </Box>
                            ) : null}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                ) : null}

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Usage
                  </Typography>
                  <Typography variant="body2" component="pre" sx={classes.preformattedBlock}>
                    {prettyPrintJson(log.usage)}
                  </Typography>
                </Box>

                {log.error ? (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Error
                    </Typography>
                    <Typography variant="body2" component="pre" sx={classes.preformattedBlock}>
                      {prettyPrintJson(log.error)}
                    </Typography>
                  </Box>
                ) : null}
              </Paper>
            );
          })}
        </div>

        {hasLoaded && logs.length === 0 && !isLoading ? (
          <Typography variant="body2" color="text.secondary">
            No logs found for the selected filters.
          </Typography>
        ) : null}

        {nextToken ? (
          <div style={classes.loadMoreContainer}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => fetchLogs({ token: nextToken })}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? 'Loading...' : 'Load more'}
            </Button>
          </div>
        ) : null}
      </div>
    </>
  );
}

export default SuggestOpportunitiesLogs;
